package com.vizio.redwolf.pay.client

import com.vizio.redwolf.pay.model.AddCreditCardClientRequestInfo
import com.vizio.redwolf.pay.model.DefaultCreditCardInfo
import com.vizio.redwolf.pay.model.UpdateCreditCardInfo
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.CONTENT_TYPE_APP_JSON_UTF8
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.SUBSCRIPTION_API_KEY
import com.vizio.redwolf.utils.applyAppConfig
import com.vizio.redwolf.utils.applyTimeout
import com.vizio.redwolf.utils.applyUserBearerToken
import com.vizio.redwolf.utils.setTraceId
import com.vizio.redwolf.utils.safeHttpCall
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.HttpHeaders
import io.ktor.http.URLProtocol
import io.ktor.http.path
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.encodeToJsonElement

/**
 * References:
 *  https://lucid.app/lucidchart/d16d7d65-df24-49e8-9ee4-2228bbe84d0b/edit
 *  https://app-vizio-ecommerce-wallet-dev.azurewebsites.net/swagger/index.html
 */
class WalletAPIHttpClient(private val payEnv: PayEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig) :
    PayApiHttpClient(clientConfig = clientConfig) {

    private val payPath = payEnv.walletPath
    private val walletApiKey = payEnv.vizioApiSubscriptionKey

    /**
     * This token is used to display the Braintree drop-in UI
     */
    suspend fun getClientToken(traceId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/credit-cards/client-token")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, walletApiKey)
                }
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun getCreditCards(authToken: String, traceId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/credit-cards")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, walletApiKey)
                }
                applyUserBearerToken(authToken)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun addCreditCard(authToken: String, clientRequestInfo: AddCreditCardClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val cardRequestBody = Json.encodeToJsonElement(clientRequestInfo)
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/credit-cards")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    append(SUBSCRIPTION_API_KEY, walletApiKey)
                }
                applyUserBearerToken(authToken)
                setBody(cardRequestBody)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun deleteCreditCard(cardToken: String, authToken: String, traceId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.delete {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/credit-cards/$cardToken")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, walletApiKey)
                }
                applyUserBearerToken(authToken)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun getDefaultCreditCard(authToken: String, traceId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/credit-cards/default")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, walletApiKey)
                }
                applyUserBearerToken(authToken)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun setDefaultCreditCard(cardToken: String, authToken: String, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = Json.encodeToJsonElement(DefaultCreditCardInfo(cardToken))
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/credit-cards/default")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    append(SUBSCRIPTION_API_KEY, walletApiKey)
                }
                applyUserBearerToken(authToken)
                setBody(requestBody)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun updateCreditCard(authToken: String, cardToken: String, updateCreditCardInfo: UpdateCreditCardInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = Json.encodeToJsonElement(updateCreditCardInfo)
        return safeHttpCall {
            httpClient.put {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/credit-cards/$cardToken")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    append(SUBSCRIPTION_API_KEY, walletApiKey)
                }
                applyUserBearerToken(authToken)
                setBody(requestBody)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    suspend fun getVersion(traceId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("$payPath/version")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, walletApiKey)
                }
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

}
